import React from 'react';
import { animated, useTransition } from '@react-spring/web';
import { useOnce } from '../../../utils';

/**
 * Transition component that animates the mount and unmount of the wrapped
 * children.
 *
 * This component can take in `style` as prop, but transform, and opacity will
 * be overridden.
 *
 * If prop initialTranslate = {x: xoffset, y: yoffset} is provided it will be
 * used to move the component from the provided position on mount and then back
 * again on unmount.
 *
 * Set initialOpacity to 1 to have no transition on opacity.
 *
 * This component can take a `animationRef`: a react-spring useChain ref to
 * chain this transition together with other transitions.
 *
 * When chaining this components transition with children components
 * transitions this components transition should not be shorter and should
 * start after the other transitions to prevent unmounting the component before
 * the inner transitions is cleaned up. Also, it's transition need's start
 * before chained animations to make sure the children also is mounted.
 *
 * @example
 * ```javascript
 * // for use with animationRef:
 * useChain(
 *   isVisible
 *     ? [transitionRevealRef, otherAnimRef]
 *     : [otherAnimRef, transitionRevealRef],
 *    [0.0, 0.2]
 * );
 * ```
 */
export default function TransitionReveal({
  children,
  isVisible,
  initialTranslate: { x = '0px', y = '0px' } = { x: '0px', y: '0px' },
  initialOpacity = 0.0,
  className = null,
  style = null,
  animationRef = null,
}) {
  const hasMounted = useHasMounted();
  const transitions = useTransition(isVisible, {
    from: {
      opacity: initialOpacity,
      transform: `translate(${x}, ${y})`,
      transformOrigin: '0 0',
    },
    enter: {
      opacity: 1,
      transform: `translate(0px, 0px)`,
    },
    leave: {
      opacity: initialOpacity,
      transform: `translate(${x}, ${y})`,
    },
    ref: animationRef,
  });

  // prevent rendering server side, leave it up to client side if this shoud be rendered or not
  // to prevent hydration pass mixing up merging server side and client side rendered code
  if (!hasMounted) return null;
  return (
    <>
      {transitions(
        (props, item) =>
          item && (
            <animated.div className={className} style={{ ...style, ...props }}>
              {children}
            </animated.div>
          )
      )}
    </>
  );
}

function useHasMounted() {
  const [hasMounted, setHasMounted] = React.useState(false);
  useOnce(() => {
    setHasMounted(true);
  });
  return hasMounted;
}

/**
 * useTransitionReveal
 * @param {Object} param
 * @param {Object} param.initialTranslate
 *   @param {string} param.initialTranslate.x `120px` / `-50%`
 *   @param {string} param.initialTranslate.y `120px` / `-50%`
 * @param {number} param.initialOpacity
 * @param {boolean} param.isVisible
 * @param {React.MutableRefObject<import('@react-spring/web').SpringHandle>} param.animationRef
 */
export function useTransitionReveal({
  initialTranslate: { x = '0px', y = '0px' } = { x: '0px', y: '0px' },
  initialOpacity = 0.0,
  isVisible = false,
  animationRef = undefined,
}) {
  const transitions = useTransition(isVisible, {
    from: {
      opacity: initialOpacity,
      transform: `translate(${x}, ${y})`,
      transformOrigin: '0 0',
    },
    enter: {
      opacity: 1,
      transform: `translate(0px, 0px)`,
    },
    leave: {
      opacity: initialOpacity,
      transform: `translate(${x}, ${y})`,
    },
    ref: animationRef,
  });
  return transitions;
}
